import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Modal,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import ErrorModal from "../../ErrorModal/ErrorModal.jsx";
import ReasoningModalRecommendations from "./ReasoningModalRecommendations.jsx";
import ConfirmationModalRecommendations from "./ConfirmationModalRecommendations.jsx";
import { getUserError } from "../../../utils/errors.js";
export default function ModalComponentRecommendations({
  open,
  setOpen,
  recommendation,
  onAction,
  getStatusColor,
  refreshRecommendations,
}) {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [action, setAction] = useState("");
  const [editedText, setEditedText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loadingConfirm, setLoadingConfirm] = useState(false);

  const [loadingReasoning, setLoadingReasoning] = useState(false);
  const { t: tRec } = useTranslation("recommendations");
  const { t: tErr } = useTranslation("errors");

  useEffect(() => {
    setEditedText(recommendation?.recommendation || "");
  }, [recommendation]);

  const handleAction = (actionType) => {
    setAction(actionType);
    setIsEditing(false);
    setReason("");
    setReasonDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    setLoadingConfirm(true);
    try {
      await onAction(action, editedText, reason);
      await refreshRecommendations();
      setConfirmationOpen(false);
      setReasonDialogOpen(false);
      setOpen(false);
    } catch (error) {
      setLoadingConfirm(false);
      setErrorMsg(getUserError(tErr, action, error));
      setErrorOpen(true);
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleEdit = (event) => {
    setEditedText(event.target.value);
  };

  const handleClose = () => {
    setIsEditing(false);
    setConfirmationOpen(false);
    setReasonDialogOpen(false);
    setOpen(false);
  };

  const handleSkipReason = async () => {
    setLoadingReasoning(true);
    try {
      await onAction(action, editedText, "");
      await refreshRecommendations();
      setReasonDialogOpen(false);
      setConfirmationOpen(false);
      setOpen(false);
    } catch (error) {
      setLoadingReasoning(false);
      setErrorMsg(getUserError(tErr, action, error));
      setErrorOpen(true);
    } finally {
      setLoadingReasoning(false);
    }
  };

  return (
    <>
      {/* Main Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              {tRec("columns.recommendation")}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box id="modal-description" sx={{ mt: 2, mb: 3 }}>
            {isEditing ? (
              <TextField
                variant="outlined"
                label={tRec("labels.editRecommendation")}
                multiline
                rows={8}
                value={editedText}
                onChange={handleEdit}
                sx={{ width: "100%", mt: 2 }}
              />
            ) : (
              <>
                <Typography sx={{ fontSize: "0.95rem" }}>
                  {recommendation?.recommendation}
                </Typography>
                <Typography
                  color={getStatusColor(recommendation?.status)}
                  sx={{ fontStyle: "italic", fontSize: "0.75rem", pt: "15px" }}
                >
                  {recommendation?.status}
                </Typography>
              </>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2,
              gap: "10px",
              flexDirection: {
                xs: "column",
                sm: "column",
                md: "row",
              },
              alignItems: {
                xs: "center",
                sm: "center",
                md: "unset",
              },
            }}
          >
            {isEditing ? (
              <>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setIsEditing(false)}
                >
                  {tRec("buttons.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleAction("edit")}
                  disabled={
                    (editedText.trim() || "") ===
                      (recommendation?.recommendation?.trim() || "") ||
                    editedText === "" ||
                    editedText.length < 10
                  }
                >
                  {tRec("buttons.ok")}
                </Button>
              </>
            ) : (
              <>
                {recommendation?.status !== "Rejected" && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleAction("reject")}
                  >
                    {tRec("buttons.reject")}
                  </Button>
                )}
                {recommendation?.status !== "Accepted" && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleAction("approve")}
                    >
                      {tRec("buttons.approve")}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsEditing(true);
                      }}
                    >
                      {tRec("buttons.edit")}
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Reasoning Modal */}
      <ReasoningModalRecommendations
        reasonDialogOpen={reasonDialogOpen}
        setReasonDialogOpen={setReasonDialogOpen}
        setConfirmationOpen={setConfirmationOpen}
        action={action}
        setAction={setAction}
        reason={reason}
        setReason={setReason}
        onContinueWithoutReason={handleSkipReason}
        loading={loadingReasoning}
      />
      {/* Final Confirmation Modal */}
      <ConfirmationModalRecommendations
        confirmationOpen={confirmationOpen}
        setConfirmationOpen={setConfirmationOpen}
        action={action}
        reason={reason}
        handleConfirmAction={handleConfirmAction}
        loading={loadingConfirm}
      />

      {/* Error modal */}
      <ErrorModal
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        title={tErr("errorsRecommendation.updateTitle")}
        message={errorMsg}
      />
    </>
  );
}

// Modal Styles
export const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: {
    xs: "70%",
    sm: "60%",
    md: 700,
    lg: 700,
  },
  width: "80%",
  bgcolor: "background.paper",
  border: "unset",
   borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export const modalStyle2 = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 450,
  bgcolor: "background.paper",
  border: "unset",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};
